import { useCallback, useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import rootStore from '@/app/store'

const SCRIPT_SRC = 'https://unpkg.com/@elevenlabs/convai-widget-embed'
const AGENT_ID = 'agent_01jzsqwa3aegx82069sdczqh4e'

const addScriptToBody = (scriptEl: HTMLScriptElement) => {
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
    scriptEl.onload?.(new Event('load'))
    return
  }
  scriptEl.src = SCRIPT_SRC
  scriptEl.async = true
  scriptEl.type = 'text/javascript'
  document.body.appendChild(scriptEl)
}

// Helper para formatear la transcripción
interface TranscriptMessage {
  role: 'user' | 'assistant'
  content: string
}

const formatTranscript = (transcript: TranscriptMessage[]): string => {
  if (!Array.isArray(transcript)) {
    // Fallback por si el formato cambia o no es un array
    return typeof transcript === 'string'
      ? transcript
      : JSON.stringify(transcript, null, 2)
  }
  return transcript
    .filter((msg) => msg && msg.role && msg.content) // Filter out invalid messages
    .map((msg) => `${msg.role.toUpperCase()}: ${msg.content}`)
    .join('\n')
}

const ElevenLabsWidget = observer(() => {
  const { support } = rootStore
  const hostRef = useRef<HTMLElement & { shadowRoot: ShadowRoot }>(null)

  useEffect(() => {
    const script = document.createElement('script')
    let intervalId: ReturnType<typeof setInterval> | undefined

    script.onload = () => {
      intervalId = setInterval(() => {
        const root = hostRef.current?.shadowRoot
        if (!root) return

        const BUTTON_ID = 'aikit-elevenlabs-close-btn'
        const WIDGET_HEADER_SELECTOR =
          'div:nth-child(2) > div > div.flex.items-center'

        const widgetHeader = root.querySelector(WIDGET_HEADER_SELECTOR)

        if (widgetHeader && !widgetHeader.querySelector(`#${BUTTON_ID}`)) {
          const styleEl = document.createElement('style')
          styleEl.textContent = `
            p:has(a[href*="https://elevenlabs.io/conversational-ai"]) {
              display: none !important;
            }
            #${BUTTON_ID} {
              /* Ajustes para que encaje en la cabecera */
              background-color: #F0F2F4;
              color: #595959;
              border: 1px solid #E0E0E0;
              height: 36px;
              padding: 2px 10px;
              cursor: pointer;
              border-radius: 4px;
              font-size: 14px; /* Un poco más grande para el icono */
              margin-left: 6px;
              margin-right: 4px;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s ease;
            }
            #${BUTTON_ID}:hover {
              background-color: #d3d9de;
            }
          `
          root.appendChild(styleEl)

          const closeButton = document.createElement('button')
          closeButton.id = BUTTON_ID
          closeButton.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M3.33333 2.62622L8.16666 7.45955L13 2.62622L13.7071 3.33333L8.87377 8.16666L13.7071 13L13 13.7071L8.16666 8.87377L3.33333 13.7071L2.62622 13L7.45955 8.16666L2.62622 3.33333L3.33333 2.62622Z" fill="#595959"/>
</svg> <span style="padding-left: 4px;">Cerrar</span>`
          closeButton.addEventListener('click', (e) => {
            e.stopPropagation()
            support.setOpen(false)
          })

          widgetHeader.appendChild(closeButton)

          clearInterval(intervalId)
        }
      }, 200)
    }

    addScriptToBody(script)

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [support])

  const handleCall = useCallback(
    (event: any) => {
      if (!event.detail?.config) {
        return
      }

      event.detail.config.clientTools = {
        escalateToHuman: ({
          transcript,
        }: {
          transcript: TranscriptMessage[]
        }) => {
          try {
            console.log('ElevenLabs escalateToHuman called with:', transcript)
            const formattedTranscript = formatTranscript(transcript)
            support.openContactForm(formattedTranscript)
          } catch (error) {
            console.error('Error in escalateToHuman:', error)
            // Fallback: open form with raw transcript
            support.openContactForm(
              JSON.stringify(transcript, null, 2) ||
                'Error al procesar la transcripción',
            )
          }
        },
      }
    },
    [support],
  )

  useEffect(() => {
    document.addEventListener('elevenlabs-convai:call', handleCall)
    return () => {
      document.removeEventListener('elevenlabs-convai:call', handleCall)
    }
  }, [handleCall])

  if (!support.elevenLabsWidgetStore.isOpen) return null

  return (
    <div>
      <elevenlabs-convai
        ref={hostRef as any}
        agent-id={AGENT_ID}
      ></elevenlabs-convai>
    </div>
  )
})

export default ElevenLabsWidget
